import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { WhatsAppSettingsCard } from "@/components/app/WhatsAppSettingsCard";
import { Save, Upload, Trash2, FileText, Image as ImageIcon, Building2, Lock } from "lucide-react";

/**
 * Admin-only /general-settings page. Captures company identity + bank details +
 * three uploadable branding assets (logo, quotation start pages PDF, quotation
 * end pages PDF) that the invoice & quotation renderers can pick up globally.
 */
export default function GeneralSettings() {
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({
    company_name: "", gstin: "", address: "", phone: "", email: "", website: "",
    bank_name: "", bank_account_no: "", bank_ifsc: "", bank_branch: "", bank_upi: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/company-settings");
    setDoc(r.data);
    setForm({
      company_name: r.data.company_name || "",
      gstin: r.data.gstin || "",
      address: r.data.address || "",
      phone: r.data.phone || "",
      email: r.data.email || "",
      website: r.data.website || "",
      bank_name: r.data.bank_name || "",
      bank_account_no: r.data.bank_account_no || "",
      bank_ifsc: r.data.bank_ifsc || "",
      bank_branch: r.data.bank_branch || "",
      bank_upi: r.data.bank_upi || "",
    });
  };
  useEffect(() => { load(); }, []);

  // Non-admins get a friendly access-denied panel instead of the full form.
  if (user && user.role !== "admin") {
    return (
      <div data-testid="general-settings-page" className="p-8">
        <div className="flat-card p-10 text-center max-w-xl mx-auto">
          <Lock className="w-10 h-10 mx-auto text-[#9CA3AF] mb-3" />
          <div className="font-black text-xl mb-1">Admin Access Required</div>
          <div className="text-sm text-[#4B5563]">
            General Settings is restricted to administrators. Ask an admin to grant you the role if you need to edit company
            identity, bank details or quotation branding assets.
          </div>
        </div>
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/company-settings", form);
      toast.success("Company settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const F = (field, label, type = "text", placeholder = "") => (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input
        type={type}
        value={form[field]}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        placeholder={placeholder}
        className="rounded-none mt-1 h-10"
        data-testid={`gs-${field}`}
      />
    </div>
  );

  return (
    <div data-testid="general-settings-page">
      <PageHeader
        eyebrow="Admin · Phase Settings"
        title="General Settings"
        subtitle="Company identity, bank details and quotation branding assets used across invoices and quotation PDFs."
        action={
          <Button
            onClick={save}
            disabled={busy}
            className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6"
            data-testid="gs-save"
          >
            <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save Settings"}
          </Button>
        }
      />

      <div className="p-4 sm:p-8 space-y-6">
        {/* Company Identity */}
        <section className="flat-card p-6 space-y-4" data-testid="gs-company-section">
          <div className="label-eyebrow flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Company Identity</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {F("company_name", "Company Name", "text", "Multimix Energy Pvt Ltd")}
            {F("gstin", "GSTIN", "text", "32AAACM1234A1Z5")}
            {F("phone", "Contact Number", "tel", "+91-9876543210")}
            {F("email", "Email", "email", "info@multimixenergypvtltd.in")}
            {F("website", "Website", "url", "https://multimixenergypvtltd.in")}
            <div>
              <Label className="label-eyebrow">Address</Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={2}
                className="rounded-none mt-1 min-h-[72px]"
                placeholder="Full registered office address"
                data-testid="gs-address"
              />
            </div>
          </div>
        </section>

        {/* Bank Details */}
        <section className="flat-card p-6 space-y-4" data-testid="gs-bank-section">
          <div className="label-eyebrow flex items-center gap-2">Bank Details (printed on invoices)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {F("bank_name", "Bank Name", "text", "HDFC Bank")}
            {F("bank_account_no", "Account Number")}
            {F("bank_ifsc", "IFSC Code")}
            {F("bank_branch", "Branch")}
            {F("bank_upi", "UPI ID (optional)", "text", "multimix@hdfcbank")}
          </div>
        </section>

        {/* Assets */}
        <section className="flat-card p-6 space-y-4" data-testid="gs-assets-section">
          <div className="label-eyebrow">Branding Assets</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <AssetSlot
              kind="logo"
              label="Company Logo"
              icon={ImageIcon}
              hasAsset={doc?.has_logo}
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChanged={load}
            />
            <AssetSlot
              kind="quote_start_pdf"
              label="Quotation Start Pages PDF"
              icon={FileText}
              hasAsset={doc?.has_quote_start_pdf}
              accept="application/pdf"
              onChanged={load}
            />
            <AssetSlot
              kind="quote_end_pdf"
              label="Quotation End Pages PDF"
              icon={FileText}
              hasAsset={doc?.has_quote_end_pdf}
              accept="application/pdf"
              onChanged={load}
            />
          </div>
          <div className="text-[11px] text-[#4B5563]">
            The logo appears on invoices and quotations. Start/end PDFs are stitched before and after the generated quotation body
            (useful for cover pages, T&amp;Cs, warranty terms etc.).
          </div>
        </section>

        {doc?.updated_at && (
          <div className="text-[10px] text-[#9CA3AF] uppercase tracking-widest">
            Last updated {new Date(doc.updated_at).toLocaleString()} by {doc.updated_by || "—"}
          </div>
        )}
        <WhatsAppSettingsCard />
      </div>
    </div>
  );
}

function AssetSlot({ kind, label, icon: Icon, hasAsset, accept, onChanged }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const previewUrl = hasAsset ? `${api.defaults.baseURL}/company-settings/asset/${kind}?t=${Date.now()}` : null;
  const isImage = accept?.startsWith("image/");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await api.post("/company-settings/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onDelete = async () => {
    if (!window.confirm(`Remove the uploaded ${label}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/company-settings/asset/${kind}`);
      toast.success(`${label} removed`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#E5E7EB] p-3 space-y-2" data-testid={`gs-asset-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        {hasAsset && (
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="text-[#991B1B] hover:text-red-800"
            title={`Remove ${label}`}
            data-testid={`gs-asset-${kind}-remove`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="h-32 border border-dashed border-[#E5E7EB] bg-[#F9FAFB] flex items-center justify-center overflow-hidden">
        {hasAsset ? (
          isImage ? (
            <img src={previewUrl} alt={label} className="max-h-full max-w-full object-contain" data-testid={`gs-asset-${kind}-preview`} />
          ) : (
            <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline flex items-center gap-1.5" data-testid={`gs-asset-${kind}-preview`}>
              <FileText className="w-4 h-4" /> View uploaded PDF ↗
            </a>
          )
        ) : (
          <div className="text-xs text-[#9CA3AF]">Nothing uploaded</div>
        )}
      </div>

      <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={onPick} data-testid={`gs-asset-${kind}-input`} />
      <Button
        size="sm"
        variant="outline"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="rounded-none h-9 uppercase tracking-widest text-[10px] font-bold w-full"
        data-testid={`gs-asset-${kind}-pick`}
      >
        <Upload className="w-3 h-3 mr-1" /> {busy ? "Uploading…" : hasAsset ? "Replace" : "Upload"}
      </Button>
    </div>
  );
}
